// Pre-auth report fetch by client session id. Used on /pricing and
// /checkout/:plan so we can show personalized earnings + reuse-context
// before the user has signed in.
//
// Preview fallback: when preview mode is active (localhost dev OR a
// localStorage flag), the hook short-circuits to DEV_REPORT and skips the
// API call entirely — otherwise the components gated on `report` would
// flash empty for ~10s during retry-loading.
//
// Retry budget: the parallel-Haiku report generator takes ~25–35s. We poll
// for up to ~90s (20 attempts with mild backoff) so the user reaches
// /pricing AFTER /building-report's 25s timer and the report has time to
// finish landing in the DB. Caching is keyed on sessionId so subsequent
// page mounts (e.g. /pricing → /checkout/monthly → back) are instant.

import { useEffect, useState } from 'react';
import type { Report } from '../dashboard/reportTypes';
import { DEV_REPORT, isPreviewMode } from '../dashboard/devPreview';
import { normalizeReport } from '../dashboard/normalizeReport';
import { useQuizStore } from '../store/quizStore';

interface ApiResponse { report: Report | null; model?: string | null; error?: string }

// Cache stores the latest payload + its model so we know if it's still the
// stub-baseline (and should keep polling) or a real Claude/Gemini report
// (terminal state).
interface CacheEntry { report: Report | null; model: string | null; ts: number }
const cache = new Map<string, CacheEntry>();

const STUB_MODELS = new Set(['stub-pending', 'stub']);
const MAX_ATTEMPTS = 30;       // ~120s of polling with backoff
const BASE_INTERVAL_MS = 2000; // initial retry delay
const BACKOFF_INCREMENT_MS = 400; // each attempt adds this many ms

export function useReportBySession(): {
  report: Report | null;
  status: 'loading' | 'ready' | 'missing' | 'no-session';
} {
  const sessionId = useQuizStore((s) => s.sessionId);
  const previewMode = isPreviewMode();
  const [report, setReport] = useState<Report | null | undefined>(
    previewMode ? DEV_REPORT : sessionId ? cache.get(sessionId)?.report : undefined,
  );

  useEffect(() => {
    if (previewMode) return; // skip fetch entirely; using stub
    if (!sessionId) return;

    // Start from cache if present, but DON'T return early — we still want
    // to keep polling if the cached entry is a stub. That way late-arriving
    // real Claude data hot-swaps in without the user refreshing the page.
    const cached = cache.get(sessionId);
    if (cached) setReport(cached.report);

    let active = true;
    let attempts = 0;

    const tryFetch = async (): Promise<void> => {
      attempts += 1;
      try {
        const res = await fetch(`/api/reports-by-session?sid=${encodeURIComponent(sessionId)}`);
        if (!res.ok) throw new Error('http');
        const json = (await res.json()) as ApiResponse;
        if (!active) return;

        if (json.report) {
          // Normalize at the boundary — every consumer gets a guaranteed shape.
          const safe = normalizeReport(json.report);
          const model = json.model ?? null;
          cache.set(sessionId, { report: safe, model, ts: Date.now() });
          setReport(safe);

          // Keep polling if we got the stub baseline back — Claude/Gemini
          // might still be writing the real report in the background.
          if (model && STUB_MODELS.has(model) && attempts < MAX_ATTEMPTS) {
            window.setTimeout(tryFetch, BASE_INTERVAL_MS + attempts * BACKOFF_INCREMENT_MS);
          }
          return;
        }

        // No report yet — retry with backoff.
        if (attempts < MAX_ATTEMPTS) {
          window.setTimeout(tryFetch, BASE_INTERVAL_MS + attempts * BACKOFF_INCREMENT_MS);
        } else {
          cache.set(sessionId, { report: null, model: null, ts: Date.now() });
          setReport(null);
        }
      } catch {
        if (active && attempts < MAX_ATTEMPTS) {
          window.setTimeout(tryFetch, BASE_INTERVAL_MS + attempts * BACKOFF_INCREMENT_MS);
        } else if (active) {
          setReport(null);
        }
      }
    };
    void tryFetch();
    return () => {
      active = false;
    };
  }, [sessionId, previewMode]);

  // Preview mode short-circuits everything below.
  if (previewMode) return { report: DEV_REPORT, status: 'ready' };

  if (!sessionId) return { report: null, status: 'no-session' };
  if (report === undefined) return { report: null, status: 'loading' };
  if (report === null) return { report: null, status: 'missing' };
  return { report, status: 'ready' };
}
